import {
  CopyIcon,
  ExternalLinkIcon,
  MailIcon,
  MessageCircle,
  PhoneIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import {
  Button,
  buttonVariants,
} from "@/components/ui/button"
import { TableCell, TableRow } from "@/components/ui/table"
import { FRONTEND_ROUTES } from "@/constants"
import { formatDocument } from "@/helpers/format"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

import { CustomerCellActions } from "./cell-actions"

interface CustomersTableRowProps {
  customer: Customer
}

export const CustomersTableRow = ({
  customer,
}: CustomersTableRowProps) => {
  const { t } = useTranslation()

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: t("files.textCopied"),
      variant: "success",
    })
  }

  const whatsappNumber = `${customer.phone.countryCode}${customer.phone.areaCode}${customer.phone.number}`
  const whatsappLink = `https://wa.me/${whatsappNumber}`

  return (
    <TableRow className="even:bg-zinc-100 even:hover:bg-zinc-100">
      {/* Nome + Detalhes Mobile */}
      <TableCell className="align-top">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Link
              to={`${FRONTEND_ROUTES.CUSTOMERS.href}/details/${customer.id}`}
              className="font-medium hover:underline"
            >
              {customer.firstName} {customer.lastName}
            </Link>
          </div>

          {/* Mobile: show all info */}
          <div className="mt-3 space-y-3 border-t pt-3 lg:hidden">
            {/* ID */}
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-[10px] font-medium uppercase text-muted-foreground">
                ID
              </p>
              <div className="flex items-center gap-1">
                <p className="truncate font-mono text-xs">
                  {customer.id}
                </p>
                <button
                  type="button"
                  onClick={() => handleCopy(customer.id)}
                  className="shrink-0 rounded p-1 hover:bg-muted active:bg-muted/80"
                >
                  <CopyIcon
                    size={14}
                    className="text-muted-foreground"
                  />
                </button>
              </div>
            </div>

            {/* Documento */}
            <div className="rounded-md border p-2">
              <p className="text-[10px] font-medium uppercase text-muted-foreground">
                {t("pages.customers.table.document")}
              </p>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-xs"
                >
                  {customer.documentType}
                </Badge>
                <span className="font-mono text-xs">
                  {formatDocument(customer.document)}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    handleCopy(customer.document)
                  }
                  className="shrink-0 rounded p-1 hover:bg-muted active:bg-muted/80"
                >
                  <CopyIcon
                    size={14}
                    className="text-muted-foreground"
                  />
                </button>
              </div>
            </div>

            {/* Email */}
            <div className="rounded-md bg-muted/30 p-2">
              <p className="text-[10px] font-medium uppercase text-muted-foreground">
                {t("pages.customers.table.email")}
              </p>
              <div className="flex items-center gap-1">
                <MailIcon
                  size={12}
                  className="shrink-0 text-muted-foreground"
                />
                <p className="min-w-0 flex-1 truncate text-xs">
                  {customer.email}
                </p>
                <button
                  type="button"
                  onClick={() => handleCopy(customer.email)}
                  className="shrink-0 rounded p-1 hover:bg-muted active:bg-muted/80"
                >
                  <CopyIcon
                    size={14}
                    className="text-muted-foreground"
                  />
                </button>
              </div>
            </div>

            {/* Telefone */}
            <div className="rounded-md border p-2">
              <p className="text-[10px] font-medium uppercase text-muted-foreground">
                {t("pages.customers.table.phone")}
              </p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <PhoneIcon
                    size={12}
                    className="text-muted-foreground"
                  />
                  <span className="font-mono text-xs">
                    +{customer.phone.countryCode} (
                    {customer.phone.areaCode}){" "}
                    {customer.phone.number}
                  </span>
                </div>
                {customer.phone.whatsApp && (
                  <Button
                    variant="outline"
                    size="icon"
                    asChild
                    className="h-6 w-6"
                  >
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle
                        size={12}
                        className="text-green-600"
                      />
                    </a>
                  </Button>
                )}
              </div>
            </div>

            {/* Data de Criação */}
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-[10px] font-medium uppercase text-muted-foreground">
                {t("pages.customers.table.createdAt")}
              </p>
              <p className="text-xs text-muted-foreground">
                {DateHelper.formatDateTime(
                  customer.createdAt
                )}
              </p>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2">
              <Link
                to={`${FRONTEND_ROUTES.CUSTOMERS.href}/details/${customer.id}`}
                className={cn(
                  buttonVariants({
                    size: "sm",
                    variant: "default",
                  }),
                  "flex-1"
                )}
              >
                <ExternalLinkIcon
                  size={14}
                  className="mr-1.5"
                />
                {t("common.viewDetails")}
              </Link>
            </div>
          </div>
        </div>
      </TableCell>

      {/* Desktop columns */}
      <TableCell className="hidden align-top lg:table-cell">
        <div className="flex items-center gap-2">
          <span className="max-w-[120px] truncate font-mono text-xs">
            {customer.id}
          </span>
          <button
            type="button"
            onClick={() => handleCopy(customer.id)}
            className="shrink-0 rounded p-1 hover:bg-muted"
          >
            <CopyIcon
              size={12}
              className="text-muted-foreground"
            />
          </button>
        </div>
      </TableCell>

      <TableCell className="hidden align-top lg:table-cell">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {customer.documentType}
          </Badge>
          <span className="font-mono text-xs">
            {formatDocument(customer.document)}
          </span>
          <button
            type="button"
            onClick={() => handleCopy(customer.document)}
            className="shrink-0 rounded p-1 hover:bg-muted"
          >
            <CopyIcon
              size={12}
              className="text-muted-foreground"
            />
          </button>
        </div>
      </TableCell>

      <TableCell className="hidden align-top lg:table-cell">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {customer.email}
          </span>
          <button
            type="button"
            onClick={() => handleCopy(customer.email)}
            className="shrink-0 rounded p-1 hover:bg-muted"
          >
            <CopyIcon
              size={12}
              className="text-muted-foreground"
            />
          </button>
        </div>
      </TableCell>

      <TableCell className="hidden align-top lg:table-cell">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">
            +{customer.phone.countryCode} (
            {customer.phone.areaCode}){" "}
            {customer.phone.number}
          </span>
          {customer.phone.whatsApp && (
            <Button
              variant="outline"
              size="icon"
              asChild
              className="h-8 w-8"
            >
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle
                  size={16}
                  className="text-green-600"
                />
              </a>
            </Button>
          )}
        </div>
      </TableCell>

      <TableCell className="hidden align-top lg:table-cell">
        <span className="text-xs text-muted-foreground">
          {DateHelper.formatDateTime(customer.createdAt)}
        </span>
      </TableCell>

      <TableCell className="hidden align-top lg:table-cell">
        <CustomerCellActions customer={customer} />
      </TableCell>
    </TableRow>
  )
}
